import wootConstants from 'dashboard/constants/globals';
import {
  ASSIGNEE_TYPE_TAB_PERMISSIONS,
  CONVERSATION_PERMISSIONS,
  ROLES,
} from 'dashboard/constants/permissions';

export const hasPermissions = (
  requiredPermissions = [],
  availablePermissions = []
) => {
  return requiredPermissions.some(permission =>
    availablePermissions.includes(permission)
  );
};

export const getCurrentAccount = ({ accounts } = {}, accountId = null) => {
  return accounts.find(account => Number(account.id) === Number(accountId));
};

export const getUserPermissions = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  return currentAccount.permissions || [];
};

export const getUserRole = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  if (currentAccount.custom_role_id) {
    return 'custom_role';
  }

  return currentAccount.role || 'agent';
};

/**
 * Filters and transforms items based on user permissions.
 *
 * @param {Object} items - An object containing items to be filtered.
 * @param {Array} userPermissions - Array of permissions the user has.
 * @param {Function} getPermissions - Function to extract required permissions from an item.
 * @param {Function} [transformItem] - Optional function to transform each item after filtering.
 * @returns {Array} Filtered and transformed items.
 */
export const filterItemsByPermission = (
  items,
  userPermissions,
  getPermissions,
  transformItem = (key, item) => ({ key, ...item })
) => {
  // Helper function to check if an item has the required permissions
  const hasRequiredPermissions = item => {
    const requiredPermissions = getPermissions(item);
    return (
      requiredPermissions.length === 0 ||
      hasPermissions(requiredPermissions, userPermissions)
    );
  };

  return Object.entries(items)
    .filter(([, item]) => hasRequiredPermissions(item)) // Keep only items with required permissions
    .map(([key, item]) => transformItem(key, item)); // Transform each remaining item
};

/**
 * Resolves which conversation assignee tabs (Mine/Unassigned/All) a user may
 * see, applying the account-level hide-tabs toggles for basic agents.
 *
 * Mention/Participating views are scoped to conversations the user can already
 * see, so the toggles must not apply there: hiding Unassigned/All would strand
 * chats not assigned to the agent under an unreachable tab. Admins and custom
 * roles are never affected.
 *
 * @param {Object} params
 * @param {String} params.conversationType - Current view type (mention/participating/...).
 * @param {String} params.userRole - Resolved user role for the account.
 * @param {Object} [params.accountSettings] - Account settings holding the toggles.
 * @returns {Object} The visible subset of ASSIGNEE_TYPE_TAB_PERMISSIONS.
 */
export const getVisibleAssigneeTabPermissions = ({
  conversationType,
  userRole,
  accountSettings = {},
} = {}) => {
  const { MENTION, PARTICIPATING } = wootConstants.CONVERSATION_TYPE;
  const isPersonalScopeView = [MENTION, PARTICIPATING].includes(
    conversationType
  );

  // "Não lidas" só existe nas contas que ligaram a aba.
  const { unread, ...semNaoLidas } = ASSIGNEE_TYPE_TAB_PERMISSIONS;
  const comNaoLidas = accountSettings.unread_tab_enabled
    ? ASSIGNEE_TYPE_TAB_PERMISSIONS
    : semNaoLidas;
  // "Atendimento IA" é só de administrador, salvo quando a conta libera para agentes.
  const abas = accountSettings.show_agent_ai_tab
    ? {
        ...comNaoLidas,
        ai: {
          ...comNaoLidas.ai,
          permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
        },
      }
    : comNaoLidas;

  if (isPersonalScopeView || userRole !== 'agent') {
    return abas;
  }

  const hideUnassigned = Boolean(accountSettings.hide_agent_unassigned_tab);
  // Independentes: esconder "Não atribuídas" não esconde "Todos" (é navegação, não permissão).
  const hideAll = Boolean(accountSettings.hide_agent_all_tab);

  if (!hideUnassigned && !hideAll) return abas;

  const { unassigned, all, ...rest } = abas;
  return {
    ...rest,
    ...(hideUnassigned ? {} : { unassigned }),
    ...(hideAll ? {} : { all }),
  };
};
